/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { InteractiveComponents } from '../../../../core/enums';
import { Keys, ScreenTaskControllerTypes } from '../../../../core/navigation';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import { setActiveInteractiveComponentAction } from '../../../interactivelayer/interactivelayer.actions';
import { clearActiveInteractiveComponent } from '../../../interactivelayer/interactiveLayer.reducers';
import { GLOBAL_MOUSE_TRAP } from '../../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { onMouseClickCreateOnePassOverlay } from '../../../overlay/createonepassoverlay/createonepassoverlay.reducres';
import { onMouseClickExitConfirmationOverlay } from '../../../overlay/exitconfirmationoverlay/exitconfirmationoverlay.reducers';
import { onMouseClickModifyOnePassOverlay } from '../../../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import { onMouseClickNoNetworkOverlay } from '../../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
} from '../../../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import {
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onMouseClickRecordingOverlay,
    onMouseClickStopOrCancelRecording,
} from '../../../overlay/recordingoverlay/recordingoverlay.reducers';
import { onMouseClickRecordOverlay } from '../../../overlay/recordoverlay/recordoverlay.reducers';
import { onMouseClickSocuErrorOverlay } from '../../../overlay/socuerroroverlay/socuerroroverlay.reducres';
import { onMouseClickStorageLimitOverlay } from '../../../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import { onMouseClickVodResumeStartOverOverlay } from '../../../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import {
    hideOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../../overlaydialog/overlaydialog.reducers';
import { showWhisper } from '../../../whisper/whisper.reducer';
import { setCaretVisibilityAction } from '../filteredguide.actions';
import {
    disableGhostModeFilteredGuide,
    enableGhostModeFilteredGuide,
} from '../filteredguide.reducers';
import {
    onHorizontalKeyPressFilteredGuideAction,
    onVerticalKeyPressFilteredGuideAction,
} from './filteredguide.navigation.actions';
import { onKeyOkFilteredGuide, onLongKeyOkFilteredGuide } from './keyboard.navigation.reducers';
import {
    eventDispatcher,
    getHighlightPosition,
    onKeyOkDefaultTaskFilteredGuide,
} from './navigation.utils';

export const onMouseClickFilteredGuide = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { screenTaskController } = getState().screens;
    const position = getHighlightPosition(value);
    const { activeComponent } = getState().interactiveLayer;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onMouseClickChannelOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickCreateOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
            dispatch(onMouseClickExitConfirmationOverlay());
            break;
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickModifyOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onMouseClickOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseClickNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onMouseClickRecordOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onMouseClickSocuErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onMouseClickVodResumeStartOverOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseClickStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            if (NetworkManager.isNetworkDisconnected()) {
                dispatch(showWhisper());
                return;
            }
            if (position) {
                dispatch(onKeyOkDefaultTaskFilteredGuide());
            } else {
                dispatch(defaultMouseClickTasks(activeComponent));
            }
            break;
        default:
            if (!position) return;
            dispatch(onKeyOkFilteredGuide());
            break;
    }
};

export const onMouseClickLongPressStartFilteredGuide = () => (dispatch: Function) => {
    dispatch(onMouseClickLongPressTasks(true, false));
};

export const onMouseClickLongPressStopFilteredGuide = () => (dispatch: Function) => {
    dispatch(onMouseClickLongPressTasks(false, true));
};

export const onMouseOverFilteredGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(defaultMouseOverTasks());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        default:
    }
};

export const onMouseOutFilteredGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            dispatch(defaultMouseOutTasks());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.EXIT_CONFIRMATION_OVERLAY:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        default:
    }
};

export const onMouseClickChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { activeComponent } = getState().interactiveLayer;
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }

    if (activeComponent === InteractiveComponents.WEST) dispatch(onKeyLeftOverlayDialog());
    else if (activeComponent === InteractiveComponents.EAST) dispatch(onKeyRightOverlayDialog());
    else dispatch(onKeyOkFilteredGuide());
};

export const onMouseWheelFilteredGuide =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) return;

        if (wheelDeltaY > 0) {
            eventDispatcher(Keys.LG_REMOTE_KEY_CHANNEL_UP, true, true);
        } else if (wheelDeltaY < 0) {
            eventDispatcher(Keys.LG_REMOTE_KEY_CHANNEL_DOWN, true, true);
        }
    };

const defaultMouseOverTasks = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { ghostMode } = getState().filteredGuide;

    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setActiveInteractiveComponentAction(value));
            dispatch(setCaretVisibilityAction(true));
            dispatch(enableGhostModeFilteredGuide());
            break;
        default:
    }

    const highlightPosition = getHighlightPosition(value);

    if (!highlightPosition) {
        dispatch(enableGhostModeFilteredGuide());
        return;
    }
    if (ghostMode) dispatch(disableGhostModeFilteredGuide());

    dispatch(onHorizontalKeyPressFilteredGuideAction(highlightPosition.horizontal));
    dispatch(onVerticalKeyPressFilteredGuideAction(highlightPosition.vertical));
};

const defaultMouseOutTasks = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setCaretVisibilityAction(false));
            dispatch(disableGhostModeFilteredGuide());
            dispatch(clearActiveInteractiveComponent());
            break;
        default:
    }
};

const defaultMouseClickTasks =
    (activeComponent: InteractiveComponents, start = true, end = true) =>
    () => {
        switch (activeComponent) {
            case InteractiveComponents.NORTH:
                eventDispatcher(Keys.LG_REMOTE_KEY_CHANNEL_UP, start, end);
                break;
            case InteractiveComponents.SOUTH:
                eventDispatcher(Keys.LG_REMOTE_KEY_CHANNEL_DOWN, start, end);
                break;
            default:
        }
    };

const onMouseClickLongPressTasks = (start, end) => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { value } = getState().inputFactory;
    const { activeComponent } = getState().interactiveLayer;
    const { filteredGuideTurboScroll, filteredGuideTurboScrollDirection } =
        getState().filteredGuide;

    const position = getHighlightPosition(value);

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS:
            if (end && filteredGuideTurboScroll) {
                dispatch(defaultMouseClickTasks(filteredGuideTurboScrollDirection, false, true));
                return;
            }
            if (position) {
                dispatch(onLongKeyOkFilteredGuide());
            } else {
                dispatch(defaultMouseClickTasks(activeComponent, start, end));
            }
            break;
        default:
    }
};
